/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaEye, FaEyeSlash, FaSortDown, FaSortUp } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { updateSavedSearch } from "../../api/SavedSearches";
import { RecipientAtom } from "../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../atoms/SavedSearches";
import { UserEmailArchiveAtom } from "../../atoms/UserEmailArchiveAtom";
import useClickOutside from "../../hook/useClickOutside";
import { customTooltip } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { getScrollParent } from "../../utils/DOMHelper";
import Icon from "../Icon";
import { useModal } from "../Modal/Modal";
import SavedSearchesModal from "../NewEmail/Modals/SavedSearches";
import { TooltipText } from "../SideBar";
import { SavedSearchItem } from "./SavedSearchItem";

const InputContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const Select = styled.div`
  position: absolute;
  cursor: pointer;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;

  :hover {
    background-color: #22222217;
  }
`;

interface InputProps {
  active: boolean;
  disabled?: boolean;
  withIcon?: boolean;
  withSelect?: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  border-radius: 5px;
  outline: none;
  font-size: 16px;
  padding: 5px;
  color: #383838;
  text-indent: 5px;
  border: 1px solid #dddddd;
  padding-right: 55px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: #ffffff;

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  ${(props) =>
    props.active &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  &::placeholder {
    font-size: 16px;
    color: #979797;
  }
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
  height: 42px;
  position: relative;
`;

interface DropViewProps {
  position: { top: number; left: number };
  width: number;
  offset: number;
}

const StyledAutocompleteList = styled.div<DropViewProps>`
  position: fixed;
  background-color: #fff;
  z-index: 15;
  max-height: 320px;
  flex: 1;
  overflow: auto;
  border-radius: 4px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  padding: 10px 0px;

  top: ${(props) =>
    props.offset > 0 ? `auto` : `${props.position.top + 5}px`};
  left: ${(props) => props.position.left}px;
  width: ${(props) => props.width}px;
  bottom: ${(props) => (props.offset > 0 ? `${props.offset + 5}px` : "auto")};
`;

const ManageSavedSearch = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-top: 1px solid #ebebeb;
  margin-top: 10px;

  &:hover {
    background-color: #fff2e5;
    color: #fb972e;
  }
`;

const ManageSavedSearchTitle = styled.span`
  font-size: 16px;
  margin-left: 10px;
  font-weight: normal;
`;

interface IStyledAutocompleteSelectItemProps {
  name: string;
  value: string;
  font?: string;
  icon?: string;
  suffix?: string;
}
type IStyledAutoCompleteSelectProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  placeholder: string;
  options: IOption[];
  selectedOption: IOption;
  onOptionSelect: (opt: IOption) => void;
  disabled?: boolean;
  isFilterContactReset?: boolean;
  isFilter?: boolean;
};

export default function SavedSearchSelect(
  props: IStyledAutoCompleteSelectProps
): JSX.Element {
  const {
    options,
    selectedOption,
    onOptionSelect,
    placeholder,
    disabled,
    isFilterContactReset,
    isFilter,
  } = props;
  const [currentFocus, setCurrentFocus] = useState(-1);
  const [currentSuggestions, setCurrentSuggestions] =
    useState<IStyledAutocompleteSelectItemProps[]>();
  const [offsetPopover, setOffsetPopover] = useState(0);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState(selectedOption.name);
  const [isHoverOnInput, setIsHoverOnInput] = useState(false);
  const modal = useModal();

  const [countedSavedSearches, setCountedSavedSearches] = useRecoilState(
    CountedSavedSearchesAtom
  );

  const setRecipient = useSetRecoilState(RecipientAtom);

  /**
   * Refs to keep track
   */
  const containerRef = useClickOutside(() => setOpenDropView(false));
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const suggestionRefMap = useRef<{
    [suggestionValue: string]: HTMLDivElement | null;
  }>({});

  const [dropviewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  const dropViewRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const userEmailArchive = useRecoilValue(UserEmailArchiveAtom);

  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  function updateDropViewPosition() {
    if (!containerRef.current || !wrapperRef.current) return;
    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  useEffect(() => {
    if (!openDropView) {
      setCurrentFocus(-1);
      return () => {};
    }

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(
      containerRef.current,
      containerRef.current
    );

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
  }, [wrapperRef, openDropView]);

  useEffect(() => {
    if (!dropViewRef.current || !wrapperRef.current) return;

    const elementBoundingRect = (
      dropViewRef.current as HTMLDivElement
    ).getBoundingClientRect();

    const containerBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    let bottom = elementBoundingRect.bottom;

    if (offsetPopover !== 0)
      bottom =
        bottom + elementBoundingRect.height + containerBoundingRect.height;

    if (bottom >= 0 && bottom >= window.innerHeight) {
      setOffsetPopover(window.innerHeight - containerBoundingRect.top);
    } else {
      setOffsetPopover(0);
    }
  }, [dropViewRef, openDropView, selectedValue]);

  useEffect(() => {
    if (options) {
      setCurrentSuggestions(options);
    }
  }, [options]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
  }, []);

  useEffect(() => {
    if (!currentSuggestions?.length) return;
    if (currentFocus === -1) return;
    if (!suggestionRefMap.current) return;

    suggestionRefMap.current[
      currentSuggestions[currentFocus].name
    ]?.scrollIntoView({
      behavior: "auto",
      block: "nearest",
      inline: "start",
    });
  }, [currentFocus, suggestionRefMap, currentSuggestions]);

  useEffect(() => {
    setSelectedValue(selectedOption.name);
  }, [selectedOption]);

  useEffect(() => {
    if (!isFilterContactReset) return;
    handleItemClick({ name: "", value: "" });
  }, [isFilterContactReset]);

  function handleItemClick(option: IOption) {
    onOptionSelect({ name: option.name, value: option.value });
    setSelectedValue(option.name);
    setOpenDropView(false);
    setIsHoverOnInput(false);
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    const lastItem = currentSuggestions.length - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (currentFocus === lastItem) {
        setCurrentFocus(0);
      } else {
        setCurrentFocus(currentFocus + 1);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();

      if (currentFocus === 0 || currentFocus === -1) {
        setCurrentFocus(lastItem);
      } else {
        setCurrentFocus(currentFocus - 1);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (currentFocus > -1) {
        const opt = currentSuggestions[currentFocus] as IOption;
        handleItemClick(opt);
      } else {
        handleItemClick({ name: "", value: "" });
      }
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setCurrentFocus(-1);
    setOpenDropView(true);
    setSelectedValue(value);

    const suggestionsArray = options
      .map((suggestion, index) => ({
        ...suggestion,
        index,
        isActive: false,
      }))
      .filter((suggestion) => {
        return suggestion.name
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase().trim());
      });

    updateDropViewPosition();
    setCurrentSuggestions(suggestionsArray);

    if (value === "") {
      onOptionSelect({
        value: "",
        name: "",
      });
    }
  }

  function onFocus() {
    const suggestionsArray = options.map((suggestion, index) => ({
      ...suggestion,
      index,
      isActive: false,
    }));
    updateDropViewPosition();
    setCurrentSuggestions(suggestionsArray);
    setOpenDropView(true);
  }

  useEffect(() => {
    if (!openDropView) {
      setTimeout(() => {
        if (selectedValue !== "" && selectedOption.name === "") {
          setSelectedValue("");
        } else if (selectedValue !== "" && selectedOption.name !== "") {
          setSelectedValue(selectedOption.name);
        }
      }, 100);
    }
  }, [openDropView]);

  const currentSavedSearch = countedSavedSearches.find(
    (search) => search.name === selectedOption.name
  );

  async function updateShowOnArchive(isArchived: boolean) {
    const isSuccessUpdateSearch = await updateSavedSearch(
      currentSavedSearch.id,
      {
        ...currentSavedSearch,
        publicArchive: isArchived,
      }
    );

    if (isSuccessUpdateSearch) {
      setCountedSavedSearches((s) =>
        s.map((searchFromMap) =>
          searchFromMap.id === currentSavedSearch.id
            ? {
                ...currentSavedSearch,
                publicArchive: isArchived,
              }
            : searchFromMap
        )
      );

      setRecipient((r) => ({
        ...r,
        showOnArchive: isArchived,
      }));
    }
  }

  return (
    <AutoCompleteDiv
      ref={containerRef}
      onMouseEnter={() => {
        setIsHoverOnInput(true);
      }}
      onMouseLeave={() => {
        setIsHoverOnInput(false);
      }}
    >
      {(isHoverOnInput || openDropView) && selectedValue !== "" && (
        <Select
          css={css`
            ${userEmailArchive && userEmailArchive.enabled && currentSavedSearch
              ? css`
                  right: 65px;
                `
              : css`
                  right: 35px;
                `}
            top: 10px;
          `}
          onClick={(e) => {
            e.stopPropagation();
            handleItemClick({ name: "", value: "" });
          }}
        >
          <IoMdClose color="#777777" size={16} />
        </Select>
      )}

      {userEmailArchive &&
        userEmailArchive.enabled &&
        currentSavedSearch &&
        !isFilter && (
          <>
            <Select
              data-tip
              data-for="email-archives"
              css={css`
                right: 35px;
                top: 10px;
              `}
              onClick={() =>
                updateShowOnArchive(!currentSavedSearch.publicArchive)
              }
            >
              {currentSavedSearch.publicArchive ? (
                <FaEye color="#fb972e" size={16} />
              ) : (
                <FaEyeSlash color="rgb(187, 187, 187)" size={18} />
              )}
            </Select>
            <ReactTooltip
              id="email-archives"
              place="top"
              effect="solid"
              globalEventOff="click"
              backgroundColor="#333333"
              css={customTooltip}
            >
              <TooltipText>
                {currentSavedSearch.publicArchive
                  ? "Showing on archive"
                  : "Hidden from archive"}
              </TooltipText>
            </ReactTooltip>
          </>
        )}

      <Select
        css={css`
          right: 10px;
          top: 10px;
        `}
        onClick={(e) => {
          if (inputRef.current && !openDropView) inputRef.current.focus();
          else setOpenDropView(false);
          e.stopPropagation();
        }}
      >
        {openDropView ? (
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <FaSortUp color="#777777" size={14} />
          </div>
        ) : (
          <div
            css={css`
              margin-bottom: 4px;
            `}
          >
            <FaSortDown color="#777777" size={14} />
          </div>
        )}
      </Select>

      <InputContainer ref={wrapperRef}>
        <Input
          ref={inputRef}
          active={openDropView}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={onFocus}
          value={selectedValue}
          type="text"
          placeholder={placeholder}
          autoComplete="off"
        />
        {openDropView && (
          <StyledAutocompleteList
            offset={offsetPopover}
            ref={dropViewRef}
            position={dropviewPosition}
            width={dropViewWidth}
          >
            {currentSuggestions.length > 0 ? (
              currentSuggestions.map((suggestionItem, index) => {
                return (
                  <div
                    key={suggestionItem.name + index}
                    ref={(r) =>
                      (suggestionRefMap.current[suggestionItem.name] = r)
                    }
                  >
                    <SavedSearchItem
                      onClick={() => handleItemClick(suggestionItem)}
                      name={suggestionItem.name}
                      isHover={currentFocus === index}
                      isSelected={selectedOption.value === suggestionItem.value}
                      alreadyWrittenText={selectedValue}
                      icon={suggestionItem.icon}
                      suffix={suggestionItem.suffix}
                      font={suggestionItem.font}
                    />
                  </div>
                );
              })
            ) : (
              <SavedSearchItem
                noOption
                onClick={() => null}
                name={"No options"}
                isHover={false}
                isSelected={false}
                alreadyWrittenText={selectedValue}
              />
            )}
            {options!.length > 0 && (
              <ManageSavedSearch
                onClick={() => {
                  modal.pushModal(<SavedSearchesModal isEdit />, "", true);
                }}
              >
                <Icon name="settings" width={15} height={15} />

                <ManageSavedSearchTitle>
                  Manage Saved Searches
                </ManageSavedSearchTitle>
              </ManageSavedSearch>
            )}
          </StyledAutocompleteList>
        )}
      </InputContainer>
    </AutoCompleteDiv>
  );
}
